import { Link } from 'react-router';
import type { UndefinedOnPartialDeep } from 'type-fest';

import Text from '@ingka/text';
import { selectConnectedOrders } from '@repo/api-adapters/selectors/customer-assets.selectors';
import { translate } from '@repo/phrase';
import { assetTypes } from '@repo/types/asset';
import type { Service } from '@repo/types/service';
import { Heading } from '@repo/ui/typography';
import { formatDateTime } from '@repo/utils';

import { ArticleDeliveryView } from '@/components/services-table/article-list-modal/article-delivery-view/article-delivery-view.component';
import { ArticleDetailsView } from '@/components/services-table/article-list-modal/article-details-view';
import { ArticleServiceView } from '@/components/services-table/article-list-modal/article-service-view';

import { useAsset, useAssetParams } from '@/hooks/asset.hooks';
import { useAppBaseUrl } from '@/hooks/router.hooks';
import { getServicePaymentTranslation } from '@/utils/translation.utils';
import { getAppConfig } from '@/utils/window.utils';

import styles from '../styles.module.scss';

interface Props {
    service: UndefinedOnPartialDeep<Service> | undefined;
    toggleVisible: () => void;
}

interface ConnectedOrder {
    orderNumber: string;
}

export const ArticleServiceDetails = ({ service, toggleVisible }: Props) => {
    const { assetType, assetId } = useAssetParams();
    const { data } = useAsset(assetType, assetId);
    const baseUrl = useAppBaseUrl();

    if (!service) {
        return;
    }

    const { isDelivery, referenceItems, timeWindow } = service;

    const isOrder = assetType === assetTypes.order;
    const connectedOrders = selectConnectedOrders(data);
    const mergedOrders = connectedOrders
        ? connectedOrders.filter(
              (order): order is ConnectedOrder =>
                  order.connectionType === 'MERGE_CONNECTION' && Boolean(order.orderNumber)
          )
        : [];
    const hasMergedOrders = mergedOrders.length > 0;
    const serviceProviderPreferredPayment = getServicePaymentTranslation(service.servicePaymentTo, isOrder);

    return (
        <>
            {isDelivery ? <ArticleDeliveryView service={service} /> : <ArticleServiceView service={service} />}
            {referenceItems && <ArticleDetailsView referenceItems={referenceItems} />}
            {isOrder && isDelivery && (
                <div className={styles.article_list_container} style={{ justifyContent: 'space-between' }}>
                    {timeWindow?.paymentCutOffDateTimeUtc && (
                        <div className={styles.article_payment_date} data-testid="article-payment-date">
                            <Heading headingSize="xs">{translate('services_article_list_modal_payment')}</Heading>
                            <Text>
                                {formatDateTime(
                                    timeWindow.paymentCutOffDateTimeUtc,
                                    getAppConfig().dateTimeFormat,
                                    timeWindow.timeZone
                                )}
                            </Text>
                        </div>
                    )}
                    {hasMergedOrders && (
                        <div className={styles.article_merged_order} data-testid="article-merged-order">
                            <Heading headingSize="xs">{translate('services_article_list_modal_merged_order')}</Heading>
                            {mergedOrders.map((mergedOrder) => {
                                const { orderNumber } = mergedOrder;

                                return (
                                    <Link
                                        onClick={() => {
                                            toggleVisible();
                                        }}
                                        to={`${baseUrl}/view/${assetTypes.order}/${orderNumber}`}
                                        key={orderNumber}
                                    >
                                        {orderNumber}
                                    </Link>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
            {isOrder && !isDelivery && (
                <div className={styles.article_payment_date} data-testid="article-payment-to">
                    <Heading headingSize="xs">{translate('services_table_pay')}</Heading>
                    <Text>{serviceProviderPreferredPayment}</Text>
                </div>
            )}
        </>
    );
};
